import { Checkbox } from '../../../components/Checkbox'
import { useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { graphql, useFragment } from '../../../gql'
import { useGQLMutation } from '../../../hooks/GQLUtilityHooks'
import { useGQLFetcher } from '../../../context/GQLClientProvider'
import { useOrderDetailContext } from '../OrderDetailContext'
import { notifyPickingActivity } from '../lib/hooks/pickingActivityEvents'
import { toastError } from '../../../lib/toastHelpers'

const updateOrderItemMutation = graphql(`
  mutation UpdateOrderItemPacking($id: Int!, $packed: Boolean!) {
    updateOrderItem(id: $id, packed: $packed) {
      data {
        id
        packed
      }
      errors
    }
  }
`)

const updateClearanceItemMutation = graphql(`
  mutation UpdateClearanceItemPackingReset($id: String!, $order: Int!, $packed: Int) {
    updateClearanceItem(id: $id, order: $order, packed: $packed) {
      data {
        id
        packed
        amount
      }
      errors
    }
  }
`)

export const PackingCheckboxCell_OrderFragment = graphql(`
  fragment PackingCheckboxCell_OrderFragment on Order {
    id
    items {
      id
      packed
      clearanceItems {
        id
      }
    }
    processStatus {
      id
    }
  }
`)

export function PackingCheckboxCell({ orderId, orderItemId }: { orderId: number; orderItemId: number }) {
  const queryClient = useQueryClient()

  const context = useOrderDetailContext()
  const order = useFragment(PackingCheckboxCell_OrderFragment, context.order)

  const orderItem = useMemo(() => order?.items?.find(el => el.id === orderItemId), [order, orderItemId])
  const updateClearanceItemFetcher = useGQLFetcher(updateClearanceItemMutation)

  const { mutate, isPending: isLoading } = useGQLMutation(updateOrderItemMutation, ['updateOrderItem', orderItemId], {
    onSuccess: (data, variables) => {
      queryClient.setQueryData(['order', { id: order.id }], (old: any) => {
        return {
          order: {
            ...old.order,
            data: {
              ...old.order.data,
              items: old.order.data.items.map((oldItem: any) =>
                oldItem.id === orderItemId ? { ...oldItem, ...(data ?? {}) } : oldItem,
              ),
            },
          },
        }
      })
      notifyPickingActivity({ orderId: Number(order.id), phase: 'packing', source: 'checkbox' })

      // When the checkbox is unchecked, reset the clearance items to 0
      if (variables.packed === false) {
        const item = order.items.find(el => el.id === orderItemId)
        const clearanceItemIds = item?.clearanceItems?.map(ci => ci.id).filter(Boolean) ?? []
        if (clearanceItemIds.length > 0) {
          Promise.all(
            clearanceItemIds.map(id => updateClearanceItemFetcher({ id: id!, order: order.id, packed: 0 })),
          ).then(() => {
            queryClient.setQueryData(['order', { id: order.id }], (old: any) => {
              const idsSet = new Set(clearanceItemIds)
              return {
                order: {
                  ...old.order,
                  data: {
                    ...old.order.data,
                    clearanceItems: old.order.data.clearanceItems.map((ci: any) =>
                      idsSet.has(ci.id) ? { ...ci, packed: 0 } : ci,
                    ),
                  },
                },
              }
            })
          })
        }
      }
    },
    onError: error => {
      console.log('error', error)
      toastError(error.message)
    },
  })

  const handleToggle = () => {
    const item = order.items.find(el => el.id === orderItemId)
    mutate({
      id: orderItemId,
      packed: !item.packed,
    })
  }

  if (order.processStatus.id !== 'packing') return null

  if (isLoading)
    return (
      <div className={'flex items-center justify-center flex-1 h-8'}>
        <div className="loader-small ease-linear" />
      </div>
    )

  return (
    <div className={'flex items-center justify-center flex-1 h-8'}>
      <Checkbox checked={orderItem?.packed} onChange={handleToggle} />
    </div>
  )
}
